import { useState } from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { AgeBucket, type FigurePreference, type Sex } from "@heraion/shared";
import { SettingsPillChoice, resolveTheme, type UiTheme } from "@heraion/ui";
import { useThemePalette } from "./theme";
import { useShellProfile } from "./ThemeProfileBridge";

// Settings → Personal details on the web console: the rarely-changed
// physical-identity fields (biological sex for the Navy body-fat math,
// optional age bucket, the muscle-map figure). Mirror of mobile's
// SettingsBody. Measurements and the pain map are Me rows (#315).

const BUCKETS = AgeBucket.options;
const FIGURE_FOR_SEX: Record<Sex, FigurePreference> = {
  male: "masculine",
  female: "feminine",
};

const ageLabel = (b: string): string => {
  if (b === "under_18") return "<18";
  if (b === "70-plus") return "70+";
  return b.replace("-", "–");
};

export function BodyScreen() {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();
  const { doc, loading, error, saveField, savePatch } = useShellProfile();
  const [savingField, setSavingField] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const styles = makeStyles(theme);

  const sex = (doc?.["sex"] as Sex | undefined) ?? null;
  const ageBucket = (doc?.["ageBucket"] as string | undefined) ?? null;
  const figure = (doc?.["preferredFigure"] as FigurePreference | undefined) ?? "masculine";

  const run = async (field: string, fn: () => Promise<void>) => {
    setSavingField(field);
    setSaveError(null);
    try {
      await fn();
    } catch {
      setSaveError("api_error_save");
    } finally {
      setSavingField(null);
    }
  };

  const onPickSex = (next: Sex) =>
    void run("sex", () => {
      const patch: Record<string, unknown> = { sex: next };
      if (doc?.["preferredFigure"] === undefined) patch["preferredFigure"] = FIGURE_FOR_SEX[next];
      return savePatch(patch);
    });

  const onPickAge = (next: string) =>
    void run("ageBucket", () =>
      saveField("ageBucket", ageBucket === next ? undefined : next),
    );

  const onPickFigure = (next: FigurePreference) =>
    void run("preferredFigure", () => saveField("preferredFigure", next));

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.palette.ink2} />
      </View>
    );
  }

  const errFor = (field: string) => (savingField === field ? saveError : error);

  return (
    <ScrollView style={styles.flex} contentContainerStyle={styles.body}>
      <SettingsPillChoice<Sex>
        theme={theme}
        label={t("mobile.body.sexLabel", { defaultValue: "Sex" })}
        help={t("mobile.body.sexHelp", {
          defaultValue:
            "Used only for the body-fat estimate. The body-map figure is a separate choice below.",
        })}
        options={[
          { value: "male", label: t("mobile.body.sex_male", { defaultValue: "Male" }) },
          { value: "female", label: t("mobile.body.sex_female", { defaultValue: "Female" }) },
        ]}
        value={sex}
        onPick={onPickSex}
        saving={savingField === "sex"}
        error={errFor("sex")}
      />

      <SettingsPillChoice<string>
        theme={theme}
        label={t("mobile.body.ageLabel", { defaultValue: "Age" })}
        help={t("mobile.body.ageHelp", { defaultValue: "Optional. Tap again to clear." })}
        options={BUCKETS.map((b) => ({ value: b, label: ageLabel(b) }))}
        value={ageBucket}
        onPick={onPickAge}
        saving={savingField === "ageBucket"}
        error={errFor("ageBucket")}
        compact
      />

      <SettingsPillChoice<FigurePreference>
        theme={theme}
        label={t("profile.preferredFigure")}
        help={t("profile.preferredFigureHelp")}
        options={[
          { value: "masculine", label: t("profile.figure_masculine") },
          { value: "feminine", label: t("profile.figure_feminine") },
        ]}
        value={figure}
        onPick={onPickFigure}
        saving={savingField === "preferredFigure"}
        error={errFor("preferredFigure")}
      />
    </ScrollView>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s } = theme;
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: p.bg },
    body: { paddingHorizontal: s.s5, paddingVertical: s.s4, gap: s.s3 },
    center: { flex: 1, alignItems: "center", justifyContent: "center", backgroundColor: p.bg },
  });
}
